import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Container,
  Alert,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

const parameters = [
  { name: 'encryptedSecret', type: 'string', description: 'The encrypted content of the secret' },
  { name: 'maxReadCount', type: 'number', description: 'Maximum number of times the secret can be viewed' },
  { name: 'validUntil', type: 'date', description: 'The expiration date for the secret' },
  { name: 'requireEmailAuth', type: 'boolean', description: 'Whether email authentication is required' },
  { name: 'authEmail', type: 'string', description: 'Optional. When set together with requireEmailAuth, only this exact email address can view the secret. Leave empty to allow any verified email.' },
  { name: 'validAt', type: 'date', description: 'The date from which the secret becomes valid' },
  { name: 'oneTime', type: 'boolean', description: 'Whether the secret is deleted after first access' },
  { name: 'burnAfterRead', type: 'number', description: 'Seconds after which the view page auto-reloads. Each reload counts as a read. Set 0 to disable. Best combined with oneTime.' },
  { name: 'iv', type: 'string', description: 'Base64 of the 96-bit (12-byte) nonce used for AES-256-GCM' },
];

const exampleRequest = {
  encryptedSecret: "U2VjcmV0TWVzc2FnZQ==",
  maxReadCount: 3,
  validUntil: "2025-06-01T00:00:00Z",
  requireEmailAuth: true,
  authEmail: "recipient@example.com",
  validAt: "2025-04-16T00:00:00Z",
  oneTime: false,
  burnAfterRead: 0,
  iv: "cmFuZG9tSVYxMjM0NTY3OA==",
};

const exampleResponse = {
  success: true,
  message: 'Secret created successfully.',
  url: 'https://share-a-secret.com/view/2347fgsh453b',
};

export default function ApiDocs() {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          API Documentation: <code>POST /secrets</code>
        </Typography>

        <Typography variant="body1" gutterBottom>
          <strong>Note:</strong> Our API only supports the creation of secret links for a secret. It does not support retrieval of secrets.
        </Typography>

        <Typography variant="body1" gutterBottom>
          This endpoint allows you to create a new encrypted secret with various configuration options.
          The API returns an URL that can be used to access the secret.
        </Typography>

        <Alert severity='info' sx={{ mt: 2 }}>
          By using our API, you agree to our <Link href="/terms-of-use" target="_blank" rel="noopener">Terms of Use </Link> and Privacy Policy. Please ensure you understand and comply with them.
        </Alert>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6">Parameters</Typography>
          <Table sx={{ mt: 2 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters.map(param => (
                <TableRow key={param.name}>
                  <TableCell><code>{param.name}</code></TableCell>
                  <TableCell>{param.type}</TableCell>
                  <TableCell>{param.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Never send the decryption key to the server. The key is only used on the client side to decrypt the secret. You must encrypt the secret on the client side and then send the encrypted secret to the server. We are not responsible for any potential data leaks caused by improper implementation.
          </Alert>
          <Typography variant="h6">How to Encrypt the Secret</Typography>
          <Typography variant="body1" gutterBottom>
            Before sending the secret to the API, encrypt it client-side with <strong>AES-256-GCM</strong> using a randomly generated 256-bit key and a 96-bit (12-byte) nonce. Send the base64 ciphertext (with the GCM auth tag appended) as <code>encryptedSecret</code> and the base64 nonce as <code>iv</code>. The random key is never sent to the server — you place it in the URL fragment (after <code>#k=</code>) so only the recipient can decrypt.
          </Typography>
          
          <Accordion>
            <AccordionSummary aria-controls="example-content" id="example-header">
              <Typography variant="body1">Example Code (Client-Side)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper sx={{ p: 2, mt: 1, backgroundColor: '#0d1117', color: '#c9d1d9', fontFamily: 'monospace' }}>
                <pre>{`// Example of encrypting a secret with AES-256-GCM
const crypto = require('crypto');

const secret = "This is a secret!";
const key = crypto.randomBytes(32); // 256-bit random key (never sent to server)
const iv = crypto.randomBytes(12);  // 96-bit nonce for AES-GCM

// Encrypt with AES-256-GCM
const cipher = crypto.createCipheriv('aes-256-gcm', key, iv);
const ciphertext = Buffer.concat([cipher.update(secret, 'utf8'), cipher.final()]);
const authTag = cipher.getAuthTag();
// Web Crypto expects the auth tag appended to the ciphertext
const encryptedSecret = Buffer.concat([ciphertext, authTag]).toString('base64');

const requestData = {
  encryptedSecret,
  iv: iv.toString('base64'),
  maxReadCount: 3,
  validUntil: "2025-06-01T00:00:00Z",
  requireEmailAuth: true,
  authEmail: "recipient@example.com",
  validAt: "2025-04-16T00:00:00Z",
  oneTime: false,
  burnAfterRead: 0,
};

// Create the secret
fetch('https://share-a-secret.com/secrets', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(requestData),
})
  .then(response => response.json())
  .then(data => {
    // Append the key to the returned URL fragment before sharing it:
    const shareUrl = data.url + '#k=' + key.toString('base64');
    console.log('Share this link:', shareUrl);
  })
  .catch(err => console.error('Error:', err));`}</pre>

              </Paper>
            </AccordionDetails>
          </Accordion>
          <Typography variant="body1" sx={{ mt: 1 }}>
  You can also use our PowerShell Module on the PowerShell Gallery.</Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6">Example Request</Typography>
          <Paper sx={{ p: 2, mt: 1, backgroundColor: '#0d1117', color: '#c9d1d9', fontFamily: 'monospace' }}>
            <pre>{JSON.stringify(exampleRequest, null, 2)}</pre>
          </Paper>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6">Example Response</Typography>
          <Typography variant="body1" gutterBottom>
            The API will return a JSON response indicating the success of the operation and the URL to access the secret.
            You have to append the <code>#</code> and the decryption key to the URL to access the secret before sending the full link to the recipient.
            Otherwise the secret can't be decrypted.
          </Typography>
          <Paper sx={{ p: 2, mt: 1, backgroundColor: '#0d1117', color: '#c9d1d9', fontFamily: 'monospace' }}>
            <pre>{JSON.stringify(exampleResponse, null, 2)}</pre>
          </Paper>
        </Box>

      </Paper>
    </Container>
  );
}
